/**
 * A 60 x 40 mm-es névtábla nyomtatása a pultnál (#168).
 *
 * MIÉRT A BÖNGÉSZŐBŐL. Norbert döntése, 2026-09-14: az esemény pultjánál két
 * laptop áll, és egy közös hálózati nyomtató szolgálja ki mindkettőt. Ha a
 * nyomtatás a böngészőből megy, nincs mit telepíteni, a két gép viselkedése
 * azonos, és a nyomtató az operációs rendszeré marad. Külső könyvtár nélkül:
 * a névtábla két szöveges sor, nem PDF-generálási feladat.
 *
 * MIÉRT VAN KÉT RÉSZRE BONTVA. A {@link buildBadgeHtml} **tiszta függvény** —
 * ugyanarra a bemenetre ugyanazt a dokumentumot adja, mellékhatás nélkül —,
 * a {@link printBadge} pedig csak a keretet intézi: rejtett iframe, `print()`,
 * takarítás. Ez nem stílus kérdése: a `window.print()` jsdom alatt nincs
 * megvalósítva, tehát a nyomtatott TARTALOM csak akkor mérhető próbából, ha
 * önmagában is előállítható. A #168 három próbafájlja közül kettő a tiszta
 * függvényre néz.
 *
 * AMI KÓDBÓL NEM DŐL EL. A lapméretet a valódi nyomtató dönti el — a böngésző
 * lapméret-kezelése gépenként és nyomtatómodellenként eltér, és ezt egyetlen
 * próba sem bizonyítja. **Az alábbi méretek a kliens saját nyomtatóján
 * végzett próbanyomtatásig ideiglenesek**, ezért állnak megnevezett
 * állandóként, egy helyen, és nem szétszórva a stíluslapban.
 */

/**
 * A címke fizikai mérete.
 *
 * A kliens döntése, 2026-09-17: 60 x 40 mm. Ez NEM a korábbi 70 x 35 mm
 * átméretezése, hanem más arány — 2:1 helyett 3:2 —, tehát a címke 10 mm-rel
 * keskenyebb és 5 mm-rel magasabb.
 *
 * A KÜSZÖBÖK A LEGHOSSZABB SZÓRA NÉZNEK, NEM CSAK A NÉV HOSSZÁRA. Ez a
 * méretváltáskor derült ki, és egy RÉGÓTA MEGLÉVŐ hibát javít.
 *
 * A sorok számát nem a név teljes hossza hajtja, hanem a leghosszabb
 * összefüggő szava: ha az maga nem fér egy sorba, a `break-word` kettévágja,
 * és a név eggyel több sorba kerül. „Ana Konstantinović" 18 karakter, tehát a
 * régi küszöb szerint a legnagyobb betűt kapta — a 14 betűs vezetéknév viszont
 * 8 mm-en két sor, az „Ana" a harmadik. Böngészőben mérve 37,7 mm tinta a
 * 34 mm-es belmagasságban: a név LELÓG a címkéről. A régi 70 × 35-ön ugyanez
 * a hiba megvolt (a „Bo Weiszenbacherne" ott is kilógott), csak ritkábban
 * fordult elő; a keskenyebb sor tette gyakorivá.
 *
 * Mérve, 54 mm-es belső szélességen, valódi Arial Bold-dal: 8 mm-en egyetlen
 * szó 11 karakterig fér egy sorba. A 6,5 és az 5 mm ebből arányosítva jön —
 * és ezt a {@link NAME_MAX_WORD} rögzíti.
 *
 * A függőleges költségvetés sávonként 2 / 3 / 4 sort enged (8 / 6,5 / 5 mm).
 * A küszöbök úgy állnak, hogy egyik sáv se léphesse túl a sajátját.
 */
export const BADGE_WIDTH_MM = 60;
export const BADGE_HEIGHT_MM = 40;

/** Belső margó, hogy a szöveg ne a címke széléről induljon. */
export const BADGE_PADDING_MM = 3;

/** A felső sor (cégnév) betűmérete. Egy sor, a túlcsorduló rész levágva. */
export const COMPANY_FONT_MM = 4;

/**
 * A név betűmérete, két lépcsőben csökkenve.
 *
 * A küszöbök KARAKTERSZÁMRA néznek, nem a kirajzolt szélességre: a kirajzolt
 * szélesség csak a nyomtató saját betűkészletével mérhető, ami még nincs meg.
 * A karakterszám ennél durvább, de kiszámítható, és a próbanyomtatás után egy
 * helyen hangolható.
 */
export const NAME_FONT_MM = [8, 6.5, 5.6] as const;
export const NAME_SHRINK_AT = [18, 26] as const;

/*
 * A PADLÓ ÉS KARLO „min. 24 pt"-JA — Norbert döntése, 2026-09-17.
 *
 * Karlo §5-ös specifikációja a hosszú névre „min. 24 pt"-ot ír. Ezt SZÓ
 * SZERINT nem lehet megvalósítani: 24 pt = 8,467 mm, ami NAGYOBB, mint a
 * legnagyobb névbetű (8 mm) — egy minimum, ami meghaladja a maximumot,
 * önmagában ellentmondás.
 *
 * Megmérve mégis: a 8,467 mm-es padlóra épített létrával 18 valódi névből 12
 * LELÓG a címkéről, köztük a „Kovács-Nagy Andrea". A padló ilyen magasan azt
 * jelentené, hogy a két sorba nem férő nevet nem zsugorítani, hanem VÁGNI
 * kell — ezt Karlo sehol nem írja, és a pultnál a levágott név rosszabb a
 * kisebb betűnél.
 *
 * A feloldás az egységekben van. Karlo rajzvászna ~4,29 egység/milliméter (ez
 * ugyanaz a 4,3-as szám, amit a jegyzete tévesen „4,3x nagyításnak" nevez, és
 * amit a {@link BadgePreview} kommentje kibont). Abban a mértékben a 24 egység
 * 24 / 4,29 = 5,6 mm — vagyis Karlo minimuma a mai 5 mm-es padló ENYHE
 * MEGEMELÉSE, nem a sokszorosa. Így értve a specifikáció teljesíthető, és a
 * padló 5 mm-ről 5,6 mm-re megy.
 *
 * ÁRA, mérve: a legkisebb sávban ezzel három sor fér, nem négy. A kezelhető
 * leghosszabb név ~80 karakterről ~50-re csökken — böngészőben mérve az 50
 * karakteres név még fér, az 55 már lelóg. Valódi névnél ez elérhetetlen, de
 * nem nulla, ezért áll itt kiírva.
 */

/**
 * A LEGNAGYOBB betűméret további feltétele: a leghosszabb összefüggő szó.
 *
 * 54 mm-es belső szélességen, valódi Arial Bold-dal mérve 8 mm-en 11 karakter
 * fér egy sorba, 12 már tördel.
 *
 * MIÉRT CSAK A 8 mm-ES SÁV KAP ILYEN FELTÉTELT. A költségvetés sávonként
 * 2 / 3 / 4 sort enged. A 8 mm-es sáv plafonja tehát KETTŐ, és oda egy
 * kettévágott szó már nem fér be. A 6,5 mm-es sávban a felső hosszkorlát
 * (26 karakter) és a ~13 karakteres sorkapacitás együtt legfeljebb három sort
 * enged — ami pont belefér a saját plafonjába —, az 5 mm-es sáv pedig a
 * maradék, ott a hosszra nincs korlát. Külön szófeltétel ezekben csak
 * fölöslegesen vinné le a betűméretet: „Ana Konstantinović" 6,5 mm-en három
 * sor és 32,7 mm, tehát fér.
 */
export const NAME_MAX_WORD = 11;

/**
 * A harmadik sor (résztvevő-kategória) betűmérete és betűköze.
 *
 * Karlo terve, 2026-09-16: a kategória NAGYBETŰVEL, kisebben, szintén
 * feketén kerül a név alá. Kifordított (sötét alapon fehér) sávot a kliens
 * soknak talált, ezért nincs háttér — ez egyszerre esztétikai és nyomtatási
 * döntés: a böngésző a háttérszínt alapból nem viszi ki a papírra, tehát egy
 * sötét sáv gépenként eltérően nézne ki.
 *
 * A betűköz nem díszítés: nagybetűs, rövid szó betűköz nélkül a névhez képest
 * tömbnek látszik, és a pultnál két méterről ez a sor mondja meg, kit enged
 * be a terembe az ajtóban álló.
 */
export const TYPE_FONT_MM = 3.2;
export const TYPE_LETTER_SPACING_MM = 0.6;

/**
 * A cégsor és a kategóriasor névtől mért távolsága.
 *
 * Azért állandó, és nem beégetett érték a stíluslapban, mert a függőleges
 * költségvetés csak így SZÁMOLHATÓ — a `badgePrint.test.ts` ezzel tudja
 * ellenőrizni, hogy a három sor együtt belefér-e a címke magasságába.
 */
export const LINE_GAP_MM = 1.5;

export interface BadgeContent {
  /** A felső, kisebb sor. Üres is lehet — akkor a sor nem kerül a lapra. */
  company: string;
  /** Az alsó, vastag sor. */
  name: string;
  /**
   * A név alatti, nagybetűs kategóriasor (VENDÉG / RÉSZTVEVŐ / SZERVEZŐ …).
   *
   * Opcionális, és üresen a sor SEM kerül ki — ugyanazért, amiért a cégsor
   * sem: egy üres, de helyet foglaló elem elmozdítaná a nevet a címke
   * közepéről. Ismeretlen kategóriaértéknél a hívó dolga üresen hagyni;
   * nyers `SPONSOR`-t kiírni a résztvevő mellkasára rosszabb, mint semmit.
   */
  type?: string;
}

/**
 * A névhez tartozó KIINDULÓ betűméret milliméterben.
 *
 * 2026-09-18 óta ez tipp, nem végső érték: a dokumentum a nyomtatás előtt
 * maga is megméri a nevet, és szükség esetén lejjebb lép (lásd
 * {@link nameFitScript}). A karakterszabály attól még kell — ez adja a
 * kezdőpontot, és szkript nélkül is ez a méret marad.
 *
 * Kifelé is látszik, mert a próba ezt méri közvetlenül: a kirajzolt betűméret
 * a kész HTML-ből csak sztringilleszkedéssel lenne visszaolvasható, ami a
 * stíluslap bármely átfogalmazásától elhasalna.
 */
export function nameFontMm(name: string): number {
  const value = name.trim();
  const length = value.length;
  // A leghosszabb szó, mert a `break-word` CSAK akkor vág szót ketté, ha az
  // egy szó maga nem fér a sorba — és onnantól minden név eggyel több sor.
  const longestWord = value
    .split(/\s+/)
    .reduce((max, word) => (word.length > max ? word.length : max), 0);

  if (length <= NAME_SHRINK_AT[0] && longestWord <= NAME_MAX_WORD) {
    return NAME_FONT_MM[0];
  }
  if (length <= NAME_SHRINK_AT[1]) return NAME_FONT_MM[1];
  return NAME_FONT_MM[2];
}

/**
 * A névtábla dokumentumába ágyazott illesztő szkript (#168, 2026-09-18).
 *
 * MIÉRT KELL. A {@link nameFontMm} karakterszámra néz, a tördelés viszont a
 * kirajzolt szélességen múlik. „Anna Kovácsné Tóth" 18 karakter, a leghosszabb
 * szava 8 — a szabály szerint 8 mm jár neki. Csakhogy „Anna Kovácsné" ÉS
 * „Kovácsné Tóth" is szélesebb az 54 mm-es sornál, tehát a név HÁROM sorba
 * törik, és a nyomaton a cégsor felül, a „RÉSZTVEVŐ" alul félig lemaradt.
 * A „-né" alakú magyar neveknél ez gyakori. Karakterszámból nem látszik, hogy
 * a szomszédos szavak hogyan párosodnak egy sorba — ezt csak a böngésző tudja.
 *
 * EZÉRT A DOKUMENTUM MAGA MÉR. A szkript a {@link NAME_FONT_MM} fokozatain lép
 * lejjebb a {@link nameFontMm} tippjétől, amíg a név tartalma belefér a neki
 * jutó dobozba. A mérés a nyomtató gépén, a nyomtatás betűjével történik, és
 * a karakterszabálytól független. A padló a legkisebb fokozat, lejjebb nem
 * megy — Karlo minimuma nem csökkenhet.
 *
 * MIÉRT A DOKUMENTUMBAN, ÉS NEM A HÍVÓBAN. Két okból:
 *
 * - A beágyazott szkript a dokumentum ELEMZÉSE közben fut, tehát a `load`
 *   előtt — a {@link printHtml} csak a `load` után nyomtat, így a nyomtatás
 *   mindig az illesztés UTÁN indul, és az egyetlen-nyomtatás őre érintetlen.
 * - Az előnézet ({@link BadgePreview}) ugyanezt a dokumentumot tölti be, tehát
 *   ugyanazt a méretet mutatja, amit a nyomtató kap. A hívóban illesztve a
 *   képernyő és a papír eltérne.
 *
 * MIÉRT MŰKÖDIK A `scrollHeight`. A `.name` nem flex-tároló, a szövege csak
 * LEFELÉ csordulhat túl, tehát a `scrollHeight` a teljes tartalmat méri. (A
 * kalibrációs címke `.legend`-jénél ugyanez hazudott volna — ott a flex-
 * középre igazítás mindkét irányba tolja a túlcsordulást.)
 *
 * HA A SZKRIPT NEM FUT (egy jövőbeli CSP tiltja a beágyazott szkriptet), a
 * stíluslap akkor is megvédi a cégsort és a kategóriasort: azok nem
 * zsugorodhatnak, a név a saját dobozában vágódik.
 *
 * Visszaidézőjel NEM kerülhet a szkriptbe: sablonliterálba kerül.
 */
export function nameFitScript(name: string): string {
  // A `nameFontMm` ma mindig a fokozatok egyikét adja; ha egyszer mást adna,
  // a -1-es index a `slice`-ban némán a padlóra ugrana. A legnagyobb
  // fokozatról indulni biztonságos: a szkript úgyis addig lép, amíg befér.
  const start = Math.max(
    0,
    NAME_FONT_MM.indexOf(nameFontMm(name) as (typeof NAME_FONT_MM)[number])
  );
  const sizes = JSON.stringify(NAME_FONT_MM.slice(start));
  return `(function () {
  var name = document.querySelector('.name');
  if (!name) return;
  var sizes = ${sizes};
  for (var i = 0; i < sizes.length; i++) {
    name.style.fontSize = sizes[i] + 'mm';
    if (name.scrollHeight <= name.clientHeight) return;
  }
})();`;
}

/**
 * HTML-escape.
 *
 * Nem elméleti óvatosság: a cégnév és a résztvevő neve **szabad szöveges,
 * felhasználó által beírt mező** (a `Registration.companyName` a jelentkezési
 * űrlapról jön). Egy `&`-et vagy `<`-t tartalmazó cégnév escape nélkül
 * elrontaná a címke szerkezetét, és a névtábla helyett üres lapot adna a
 * pultnál — ott, ahol már sorban állnak.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A kinyomtatandó címke teljes HTML-dokumentuma.
 *
 * Önálló dokumentum, nem a panel DOM-jának egy darabja: a panel stíluslapja
 * (Ant Design, a saját `App.css`) a címkére nem hathat, és fordítva. Ez az
 * iframe-es útvonal egyetlen valódi indoka — egy `@media print` blokk a
 * paneloldalon a panel MINDEN stílusát magával vinné a címkére.
 */
export function buildBadgeHtml(content: BadgeContent): string {
  const company = content.company.trim();
  const name = content.name.trim();

  const type = (content.type || '').trim();

  const companyLine = company
    ? `<div class="company">${escapeHtml(company)}</div>`
    : // Üres cégnév esetén a sor sem kerül ki: egy üres, de helyet foglaló
      // elem lejjebb tolná a nevet, és a név nem a címke közepén állna.
      '';

  const typeLine = type ? `<div class="type">${escapeHtml(type)}</div>` : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(name)}</title>
<style>
  @page {
    size: ${BADGE_WIDTH_MM}mm ${BADGE_HEIGHT_MM}mm;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    width: ${BADGE_WIDTH_MM}mm;
    height: ${BADGE_HEIGHT_MM}mm;
  }
  body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: ${BADGE_PADDING_MM}mm;
    font-family: Arial, Helvetica, sans-serif;
    /* A nyomtató a háttérszínt alapból nem viszi ki; a címke fehér papír. */
    color: #000;
  }
  .company {
    font-size: ${COMPANY_FONT_MM}mm;
    max-width: 100%;
    /* Egy sor, levágva: a cégnév hossza nem tolhatja le a nevet a lapról. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: ${LINE_GAP_MM}mm;
    /* NEM zsugorodhat: az overflow: hidden a flex-elem automatikus
       minimummagasságát nullára veszi, tehát e nélkül a túl magas név
       ALULRÓL ÉS FELÜLRŐL levágta ezt a sort. Mérve, nyomaton, #168. */
    flex-shrink: 0;
  }
  .type {
    font-size: ${TYPE_FONT_MM}mm;
    /* A nagybetűsítés a STÍLUSLAPÉ, nem a hívóé: a JS toUpperCase() a
       magyar és a szerb ékezetes betűkön nyelvfüggő, a text-transform
       viszont a böngésző saját, nyelvhelyes leképezését használja. */
    text-transform: uppercase;
    letter-spacing: ${TYPE_LETTER_SPACING_MM}mm;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    /* A betűköz az UTOLSÓ betű után is ott van; enélkül a sor a betűköz
       felével jobbra csúszna a névhez képest. */
    text-indent: ${TYPE_LETTER_SPACING_MM}mm;
    margin-top: ${LINE_GAP_MM}mm;
    color: #000;
    /* Ugyanazért nem zsugorodhat, mint a cégsor. */
    flex-shrink: 0;
  }
  .name {
    font-size: ${nameFontMm(name)}mm;
    font-weight: bold;
    max-width: 100%;
    text-align: center;
    /* A név TÖRHET, mert a levágott név rosszabb, mint a két sorba tördelt. */
    overflow-wrap: break-word;
    line-height: 1.1;
    /* A név az EGYETLEN elem, ami zsugorodhat: ha a legkisebb fokozaton sem
       fér, a saját dobozában vágódik, és nem a cégsort meg a kategóriasort
       tolja le a címkéről. Ez a végső háló; előtte a lenti szkript lép
       lejjebb a betűméreten. */
    min-height: 0;
    overflow: hidden;
    /* A vágás a PADDING élén történik, a 1.1-es sormagasság viszont kisebb,
       mint az Arial Bold saját magassága: vágó ráhagyás nélkül az „Á"
       ékezetének teteje és a „g" szára is lemaradt (mérve, nyomaton,
       600 dpi). A padding a vágóélt tolja kijjebb, a vele egyenlő negatív
       margó pedig kioltja — a név helye és a tördelés nem mozdul. */
    padding: 0.15em 0;
    margin: -0.15em 0;
  }
</style>
</head>
<body>
${companyLine}
<div class="name">${escapeHtml(name)}</div>
${typeLine}
<script>${nameFitScript(name)}</script>
</body>
</html>`;
}

/** Az osztások sűrűsége a kalibrációs címkén. */
export const CALIBRATION_STEP_MM = 10;

/** A kalibrációs címke feliratai — a hívó nyelvén, a szótárból. */
export interface CalibrationLabels {
  /** Rövid cím, pl. „Kalibráció". */
  title: string;
  /** Egy mondat arról, mit kezdjen vele a pultos. */
  hint: string;
}

/**
 * A KALIBRÁCIÓS címke dokumentuma (#168, Norbert kérése 2026-09-17).
 *
 * MIÉRT KÜLÖN CÍMKE, ÉS MIÉRT NEM A MINTA-TESZTCÍMKE HELYETT. A kettő két
 * KÜLÖNBÖZŐ kérdésre felel:
 *
 * - a minta-Tesztcímke azt mutatja meg, **milyen** lesz a névtábla — betűméret,
 *   tördelés, olvasható-e a kategóriasor;
 * - ez a címke azt, hogy **mekkora** — a nyomtató a névleges 60 × 40 mm-t
 *   adja-e ki, vagy skáláz, forgat, levág.
 *
 * Egy lapon a kettő elrontaná egymást: a kalibrációs keretnek a lap SZÉLÉIG
 * kell érnie, a mintának viszont épp a paddingen belül kell maradnia. Ezért
 * két külön gomb, és ezért NEM vonjuk össze őket akkor sem, ha elférnének.
 *
 * A KERET ÉS AZ OSZTÁSOK A LAP ÉLÉHEZ VANNAK HORGONYOZVA, nem a
 * {@link BADGE_PADDING_MM}-hez. A padding a TARTALOMÉ, a kalibráció a PAPÍRÉ:
 * ha az osztások a belső margóhoz igazodnának, a vonalzó a paddinget mérné, és
 * egy helyes nyomat is hibásnak látszana. Ezért abszolút pozíció és `mm`
 * mértékegység végig — semmi `%`, ami a böngésző lapméret-értelmezésétől
 * függne.
 *
 * A `@page { size: 60mm 40mm; margin: 0 }` VÁLTOZATLAN, ugyanaz, mint a
 * névtáblán: a keret ezen BELÜL rajzolódik, nem helyette. Ez a lényeg — a
 * címke azt méri, amit a nyomtató ebből a kérésből csinál.
 *
 * A KERET BELÜLRŐL rajzolódik (`box-sizing: border-box`), tehát a vonal KÜLSŐ
 * éle esik pontosan a 0 és a 60/40 mm-re. Kívülre rajzolva a vonalzó minden
 * mérésnél a vonal vastagságával többet mutatna.
 */
export function buildCalibrationHtml(labels: CalibrationLabels): string {
  // Az osztások a keret MINDEN élén: a vízszintesek a szélességet, a
  // függőlegesek a magasságot hitelesítik. A 0 és a végpont kimarad — ott a
  // keret sarka maga az osztás.
  const marks: string[] = [];
  for (let mm = CALIBRATION_STEP_MM; mm < BADGE_WIDTH_MM; mm += CALIBRATION_STEP_MM) {
    marks.push(`<div class="t top" style="left:${mm}mm"></div>`);
    marks.push(`<div class="t bottom" style="left:${mm}mm"></div>`);
  }
  for (let mm = CALIBRATION_STEP_MM; mm < BADGE_HEIGHT_MM; mm += CALIBRATION_STEP_MM) {
    marks.push(`<div class="t left" style="top:${mm}mm"></div>`);
    marks.push(`<div class="t right" style="top:${mm}mm"></div>`);
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(labels.title)}</title>
<style>
  @page {
    size: ${BADGE_WIDTH_MM}mm ${BADGE_HEIGHT_MM}mm;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    width: ${BADGE_WIDTH_MM}mm;
    height: ${BADGE_HEIGHT_MM}mm;
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
  }
  body { position: relative; }
  .frame {
    position: absolute;
    left: 0;
    top: 0;
    width: ${BADGE_WIDTH_MM}mm;
    height: ${BADGE_HEIGHT_MM}mm;
    /* Befelé rajzolva: a vonal KÜLSŐ éle esik a névleges méretre. */
    box-sizing: border-box;
    border: 0.3mm solid #000;
  }
  /*
    Az osztások a keret éléből BEFELÉ nőnek, 2 mm hosszan.

    KERETVONALLAL RAJZOLVA, NEM HÁTTÉRSZÍNNEL — és ez nem stíluskérdés.

    FIGYELEM: ez a komment a sablonliterálon BELÜL van, tehát visszaidézőjel
    nem szerepelhet benne — az lezárná a stringet, és a modul némán rosszul
    fordulna. (Egyszer már megtörtént, itt.)

    A böngésző a HÁTTÉRSZÍNT alapból NEM nyomtatja ki: a Chrome nyomtatási
    párbeszédében a „Háttérkép és szín" kikapcsolva érkezik. A névtábla
    stíluslapja ezt már tudja is („a nyomtató a háttérszínt alapból nem viszi
    ki"), csak erre az elemre nem lett alkalmazva.

    MÉRVE, 600 dpi-s nyomaton (Page.printToPDF — a valódi nyomtatási út, nem a
    képernyő elrendezése): háttérszínnel rajzolva, kikapcsolt háttérrel MIND A
    16 OSZTÁS ELTŰNT, és csak a keret maradt — az túlélte, mert az keretvonal.
    A pultos tehát egy üres téglalapot kapott volna, és pont azt nem tudta
    volna megmérni, amiért a címke létezik.

    A keretvonal nem függ a print-color-adjust tiszteletben tartásától sem;
    az egy további feltevés lenne, ez viszont mérve működik.
  */
  .t { position: absolute; box-sizing: border-box; }
  .t.top    { top: 0;    width: 0.3mm; height: 2mm; border-left: 0.3mm solid #000; }
  .t.bottom { bottom: 0; width: 0.3mm; height: 2mm; border-left: 0.3mm solid #000; }
  .t.left   { left: 0;   height: 0.3mm; width: 2mm; border-top: 0.3mm solid #000; }
  .t.right  { right: 0;  height: 0.3mm; width: 2mm; border-top: 0.3mm solid #000; }
  .legend {
    position: absolute;
    left: 0;
    top: 0;
    width: ${BADGE_WIDTH_MM}mm;
    height: ${BADGE_HEIGHT_MM}mm;
    box-sizing: border-box;
    /* A felirat az osztások VÉGÉN túl kezdődik (azok 2 mm-esek), hogy ne
       érjen hozzájuk — a vonalzó az osztást keresi, nem a szöveget. */
    padding: 4mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    /* VÁGVA, szándékosan. A felirat fordítható, és egy hosszabb nyelven
       ráfutna az osztásokra — márpedig a címke ÉRTELME az osztás. Mérve: a
       legelső szövegváltozatom 39,95 mm tintát adott a 40 mm-es lapon, azaz
       hajszálnyira volt attól, hogy elrontsa azt, amit mérni hivatott.
       Inkább csonkuljon a magyarázat, mint a mérőjel. */
    overflow: hidden;
  }
  .size { font-size: 5mm; font-weight: bold; }
  .step { font-size: 3mm; margin-top: 1mm; }
  .hint { font-size: 2.6mm; margin-top: 1.5mm; line-height: 1.2; }
</style>
</head>
<body>
<div class="frame"></div>
${marks.join('\n')}
<div class="legend">
<div class="size">${BADGE_WIDTH_MM} &times; ${BADGE_HEIGHT_MM} mm</div>
<div class="step">${escapeHtml(labels.title)} &middot; ${CALIBRATION_STEP_MM} mm</div>
<div class="hint">${escapeHtml(labels.hint)}</div>
</div>
</body>
</html>`;
}

/**
 * Hány ezredmásodpercig marad a lapon a rejtett iframe a `print()` után.
 *
 * A `print()` a legtöbb böngészőben szinkron, de a nyomtatási munka
 * összeállítása nem mindig az; az iframe azonnali eltávolítása üres lapot
 * eredményezhet. Ez a késleltetés a takarításé, nem a nyomtatásé — a felhasználó
 * semmit nem vár meg tőle.
 */
const IFRAME_CLEANUP_MS = 1000;

/**
 * Meddig várunk az iframe betöltésére, mielőtt feladjuk.
 *
 * MIÉRT KELL. A `load` esemény nem garantált: egy később bevezetett CSP
 * (`frame-src`, `sandbox`), egy böngészőbővítmény vagy egy `srcdoc`-ot
 * elutasító böngésző mellett soha nem sül el. Őr nélkül az ígéret SOHA nem
 * rendeződne — az iframe bent maradna a lapon (nyomatonként egy), a hívó
 * `printingId`-ja beragadna, és a sor Nyomtatás gombja **véglegesen
 * kattinthatatlan** lenne, amíg a kezelő újra nem tölti az oldalt. A pultnál,
 * sorban állás közben.
 *
 * Ma a panelnek nincs CSP-je, tehát ez elővigyázatosság, nem élő hiba.
 */
const IFRAME_LOAD_TIMEOUT_MS = 10000;

/**
 * Egy tetszőleges, KÉSZ dokumentum kinyomtatása a rejtett iframe-ből.
 *
 * MIÉRT VÁLT KI EZ A `printBadge`-BŐL (#168, kalibrációs címke). A `printBadge`
 * korábban maga építette a dokumentumot (`frame.srcdoc = buildBadgeHtml(...)`),
 * tehát a kereten CSAK névtábla fért át. A kalibrációs címke viszont nem három
 * szövegsor, hanem keret és osztások — nem `BadgeContent`.
 *
 * A SZÉTVÁLASZTÁS SEMMIT NEM MOZDÍT a nyomtatás viselkedésén: az iframe, a
 * `load`-őr, a hibaágak és a takarítás betű szerint ugyanaz maradt, csak a
 * dokumentum ELŐÁLLÍTÁSA került a hívóhoz. A `printBadge` változatlan aláírással
 * megmarad, és mind a három mai hívója (a sor gombja, a pult ablaka és a
 * minta-Tesztcímke) továbbra is azt hívja.
 */
export function printHtml(html: string, doc: Document = document): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const frame = doc.createElement('iframe');
    // Nem `display: none`: néhány böngésző a megjelenítésből kivett iframe-et
    // nem nyomtatja ki. A lapon kívülre tolt, nulla keretű elem viszont
    // nyomtatható, és a kezelő nem látja.
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';

    const cleanUp = () => {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    // Az őr azonnal indul, és MINDEN kimeneti ág leállítja. Enélkül a
    // `load`-ot soha meg nem kapó iframe örökre a lapon maradna.
    let settled = false;
    const watchdog = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanUp();
      reject(new Error('Printing is not available in this browser (timed out)'));
    }, IFRAME_LOAD_TIMEOUT_MS);

    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      if (error) reject(error);
      else resolve();
      setTimeout(cleanUp, IFRAME_CLEANUP_MS);
    };

    frame.onload = () => {
      /*
        MÁSODIK `load` ELLENI ŐR — és ez nem elméleti.

        A `settled` eddig csak az ígéretet védte: a `finish` másodszorra nem
        csinált semmit, a `view.print()` VISZONT MÁR LEFUTOTT. Egy iframe
        többször is kaphat `load`-ot (a böngésző újratölti, a `srcdoc` újra
        kiértékelődik), és minden ilyenkor ELMENT VOLNA EGY ÚJABB NYOMTATÁSI
        MUNKA — a pultnál egy MÁSODIK CÍMKE, csendben.

        Mérve: a `printBadge` `async`-ra váltása után a jsdom saját, valódi
        `load` eseménye is beért a mérőkeret szinkron hívása mellé, és a
        `print` kétszer hívódott. A készlet ezt fogta meg.
      */
      if (settled) return;
      try {
        const view = frame.contentWindow;
        if (!view || typeof view.print !== 'function') {
          throw new Error('Printing is not available in this browser');
        }
        view.focus();
        view.print();
        finish();
      } catch (error) {
        finish(error instanceof Error ? error : new Error(String(error)));
      }
    };

    // Második elutasítási út: a `load` elmaradhat, az `error` viszont jöhet.
    frame.onerror = () => finish(new Error('Printing is not available in this browser'));

    frame.srcdoc = html;
    doc.body.appendChild(frame);
  });
}

/**
 * Kinyomtatja a névtáblát egy rejtett iframe-ből.
 *
 * Akkor is visszatér, ha a nyomtatás nem indítható (a hívó ilyenkor sem
 * hallgat: a képernyő hibaüzenetet ír). A dobott hiba a hívóé, hogy az
 * érkeztetés és a nyomtatás sorsa külön legyen eldönthető.
 */
export async function printBadge(content: BadgeContent, doc: Document = document): Promise<void> {
  /*
    `async`, és ez nem kozmetika. A szétválasztás ELŐTT a `buildBadgeHtml`
    hívása az ígéret végrehajtójában futott, tehát egy belőle dobott hiba
    ELUTASÍTOTT ÍGÉRETKÉNT jött vissza. Kiemelve szinkron dobás lenne, amit egy
    `printBadge(x).catch(...)` alakú hívó — `try` nélkül — már nem fogna meg.
    Az `async` visszaalakítja elutasítássá, tehát a szerződés ugyanaz marad.
  */
  return printHtml(buildBadgeHtml(content), doc);
}

/**
 * Kinyomtatja a KALIBRÁCIÓS címkét (#168, Norbert kérése 2026-09-17).
 *
 * Külön gomb a minta-Tesztcímke mellett, nem helyette: lásd
 * {@link buildCalibrationHtml}.
 */
export async function printCalibrationLabel(
  labels: CalibrationLabels,
  doc: Document = document
): Promise<void> {
  // `async`, ugyanazért, amiért a `printBadge`.
  return printHtml(buildCalibrationHtml(labels), doc);
}
